/*
    pre-processing and post-processing cannot be async
    preset has to populate tableStrategy with the following:
        filters: the button + dropdown that lets users retrieve saved list filters
        search: renders the searchbar
        tabs: renders the tabs; must include a key to use to get the property for the tabs
        create: component that renders in a modal when add project button is pressed
        bulk: component that renders in a modal when bulk add button is pressed
        columns: instructions for rendering the columns

    these preset requirements are defined in ./strategies/[strategy_name]

    the component will have two props, configuration and preset
        preset will determine the base rules of the table
        configuration allows modifications to that preset?

    <ElasticTable strategy='projects' />
    <ElasticTable strategy='users' />
*/

import React, {useState, useEffect, useContext, useRef} from 'react'

import {connect} from 'react-redux'
import {Table} from 'antd'
import {onChange, update} from './onChange'
import {setStrategy, clearQuery, setTabs, setTableControls, setQueryParams, lockQueue } from './action_creators'
import FilterMeta from './FilterMeta'
import CancelModal from './CancelModal'
import ContextPopup from './ContextPopup'
import {capitalizeFirst} from '../utilities'
import DB from '../DAL/DB'
import AppContext, { initialListFilters } from '../AppContext'
import './UnifiedList.css'

const meta = new DB.Meta()

function dToP(dispatch) {
  return {
    // configure: configuration => dispatch(configure(configuration)),
    clearQuery: () => dispatch(clearQuery()),
    setStrategy: (strategy) => dispatch(setStrategy(strategy)),
    setTabs: () => dispatch(setTabs([])),
    setTableControls: (f) => dispatch(setTableControls(f)),
    setQueryParams: (obj) => dispatch(setQueryParams({...obj})),
    lockQueue: (bool) => dispatch(lockQueue(bool)),
  }
}

function sToP(state) {
  return {
    resources: state.resources,
    tableStrategy: state.tableVars.tableStrategy,
    tableControls: state.tableControls,
  }
}

function ElasticTable({location = {}, strategy = null, configuration, ...props}) {
  const {resources, tableStrategy, tableControls} = props
  const {setStrategy, setTabs, clearQuery, setQueryParams } = props // configure,

  const [modalVisible, setModalVisible] = useState(false)
  const [cancelId, setCancelId] = useState(null)
  // const [lastUpdate, setLastUpdate] = useState(null)

  const {listFilters, setListFilters} = useContext(AppContext)
  const tableControlsChanged = useRef(false)
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
  
  useEffect(
    async function () {
      // console.info('Config, Strategy changed!',strategy)
      //console.log('listFilters', listFilters)
      if (strategy) {
        if (listFilters.type && listFilters.type == strategy) {

          const obj = {}
          if (listFilters.tableControls.sorter || listFilters.tableControls.pagination) {
            tableControlsChanged.current = true 
            if (listFilters.tableControls.sorter) {
              obj.sorter = listFilters.tableControls.sorter
            }
            if (listFilters.tableControls.pagination) {
              obj.pagination = listFilters.tableControls.pagination
              if (!obj.pagination.current) obj.pagination.current = 1
            }
            //console.log('applying tableControls', obj)
            setTableControls({...obj})
          } 

         
          
          let  query = {}
          if (listFilters.query.status) {
            query.status = listFilters.query.status
          }
          if (listFilters.query.fullTextQuery) {
            query.fullTextQuery = listFilters.query.fullTextQuery
          }
          if (listFilters.query.filters) {
            query.filters = listFilters.query.filters
          }  
          query.from = listFilters.query.from ? ((obj.pagination.current - 1) * 10) : 0

          //console.log('listFilters', listFilters)
          //console.log('query', query)

          if (query.status || query.fullTextQuery || query.filters || query.from !== null) {
          //console.log('setting query', query)
          setQueryParams({...query}) 
          } 
          
          setListFilters({...initialListFilters}) 
        } 
        setStrategy(strategy)
        if (tableControlsChanged.current) {
          lockQueue(true)
          sleep(100) 
          update()
          tableControlsChanged.current = false
        }
        else {
          //console.log('normal call to onChange')
          onChange()
        }
      }

      return () => {
        setStrategy('none')
        clearQuery()
        setTabs()
      }
    },
    [configuration, strategy]
  )

  useEffect(
    function () {
      //console.info('Location changed!')
      const {savedFilter: {filters} = {}} = location.state || {}
      if (filters) {
        setTableControls({filters})
        onChange()
      }
    },
    [location.state]
  )

  // Disabled because of so many updates
  // useEffect(
  //   function () {
  //     console.info('tableStrategy changed!')

  //     return meta.subscribe('elasticUpdates', null, function ({ok}) {
  //       if (ok) {
  //         onChange()
  //       }
  //     })
  //   },
  //   [tableStrategy.type]
  // )

  const {rowClassName, rowSelection, onRow, type, columns: columnsFunction = () => null} = tableStrategy || {}
  const {pagination, filters, sorter, loading} = tableControls

  // I don't really know what to do with the cancel modal here; it's not going to show up on all the versions of the table
  const columns = columnsFunction({pagination, filters, sorter}, setModalVisible, setCancelId, onChange)

  return (
    <div className="full-width">
      <h2 className="flex-row align-start">{capitalizeFirst(type || '')}</h2>
      <div className="table-body-wrapper">
        <FilterMeta />
        <ContextPopup />
        {tableStrategy.type === strategy && (
          <Table
            style={{background: 'white'}}
            {...{
              loading,
              pagination,
              columns,
              dataSource: Object.values(resources).map((r) => ({...r, key: r.id})),
              rowSelection,
              onRow: (records) => onRow(records, setListFilters),
              onChange,
              rowClassName,
              rowkey: (record) => record.id,
            }}
          />
        )}
        <CancelModal id={cancelId} visible={modalVisible} setModalVisible={setModalVisible} />
      </div>
    </div>
  )
}

const WrappedElasticTable = connect(sToP, dToP)(ElasticTable)
export default WrappedElasticTable
